import React, { useEffect, useRef, useState } from "react";

import { gsap } from "gsap";

import { ScrollTrigger } from "gsap/ScrollTrigger";

import Header from "../../component/common/Header";

import Button from "../../component/ui/Button";

import TypewriterText from "./TypewriterText";

import Counter from "../../component/common/Counter";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const MOBILE_BREAKPOINT = 640;

const HeroSection = () => {
  const [imgLoaded, setImgLoaded] = useState(false);

  const sectionRef = useRef(null);

  const heroH1Ref = useRef(null);

  // 1. Preload Background Image

  useEffect(() => {
    const img = new Image();

    img.src = "/images/bg.webp";

    img.onload = () => setImgLoaded(true);
  }, []);

  // 2. Animation Logic

  useEffect(() => {
    // Only run if image is loaded and not on mobile

    if (!imgLoaded) return;

    const isMobile = window.matchMedia(
      `(max-width: ${MOBILE_BREAKPOINT}px)`,
    ).matches;

    if (isMobile) return;

    const context = gsap.context(() => {
      // --- Hero H1 Automatic Reveal ---

      const heroH1 = heroH1Ref.current;

      if (heroH1) {
        gsap.fromTo(
          heroH1,

          { y: 25, opacity: 0, clipPath: "inset(100% 0 0% 0)" },

          {
            y: 0,

            opacity: 1,

            clipPath: "inset(0% 0 0% 0)",

            duration: 1.5,

            ease: "power3.out",

            delay: 0.3,
          },
        );
      }

      // --- Scrubbed Content Animation (On Scroll) ---

      // Adding the specific class to your elements below

      const textElements = gsap.utils.toArray(".animated-hero-text");

      textElements.forEach((element) => {
        gsap.set(element, {
          y: 25,

          opacity: 0,

          clipPath: "inset(100% 0 0% 0)",
        });

        gsap.to(element, {
          y: 0,

          opacity: 1,

          clipPath: "inset(0% 0 0% 0)",

          scrollTrigger: {
            trigger: element,

            start: "top 85%",

            end: "top 40%",

            scrub: 1,
          },
        });
      });

      // --- Scrubbed Divider Line ---

      const lineElement = sectionRef.current.querySelector(
        ".about-card-divider",
      );

      if (lineElement) {
        gsap.set(lineElement, { scaleX: 0, transformOrigin: "left" });

        gsap.to(lineElement, {
          scaleX: 1,

          scrollTrigger: {
            trigger: ".about-card-stat-wrapper",

            start: "top 85%",

            end: "center 60%",

            scrub: 1,
          },
        });
      }
    }, sectionRef);

    return () => context.revert();
  }, [imgLoaded]); // Dependencies ensure this runs after image loads

  return (
    <section
      ref={sectionRef}
      className={`w-full min-h-screen bg-white relative transition-opacity duration-700 ${
        imgLoaded ? "opacity-100" : "opacity-0"
      }`}
      style={{
        backgroundImage: "url(/images/herobg4.png)",

        backgroundSize: "cover",

        backgroundPosition: "center",

        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="flex flex-col justify-start items-center w-full gap-[60px] sm:gap-[90px] 2xl:gap-[120px] ">
        <Header />

        <div className="w-full">
          <div
            className="flex flex-col justify-start items-center w-full gap-[124px] sm:gap-[186px] 2xl:gap-[248px]"
            style={{
              background: "linear-gradient(180deg, #00041200 0%, #000412 100%)",
            }}
          >
            {/* Main Hero Text */}

            <div className="overflow-hidden px-[32px] sm:px-0">
              <h1
                ref={heroH1Ref}
                className="text-[24px] h-[auto] lg:h-[220px] sm:text-[30px] md:text-[60px] 2xl:text-[80px] font-bold leading-[auto] md:leading-[75px] 2xl:leading-[100px] text-center text-white font-[Sora] mt-[0px] sm:mt-[97px] 2xl:mt-[130px] max-w-[100%] mx-auto"
              >
                Sustainable Energy &<br />
                Organic Growth for a Greener Tomorrow
              </h1>
            </div>

            {/* About Section Card */}

            <div className="w-full flex justify-center items-center relative">
              <div className="absolute h-[100%] w-[100%] backdrop-blur-[40px]"></div>

              <div className=" 2xl:w-full  mx-auto flex justify-center items-center relative z-10 about-card-stat-wrapper">
                <div className=" flex flex-col xl:flex-row justify-center items-center bg-[#7abf6b19] p-[32px] sm:p-[48px] 2xl:p-[64px_56px] gap-[20px]">
                  {/* Left Content (Scroll Animated) */}

                  <div className="flex flex-col justify-start items-start w-full 2xl:w-[54%] mb-8 lg:mb-0 lg:pr-8 overflow-hidden animated-hero-text">
                    <div className="overflow-hidden mb-[14px]">
                      <h2 className="text-white text-[24px] sm:text-[32px] 2xl:text-[40px] font-bold leading-[32px] sm:leading-[40px] 2xl:leading-[51px]">
                        About Govardhannathji Energies LLP
                      </h2>
                    </div>

                    <div className="overflow-hidden">
                      <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-justify text-white font-[Sora] w-[92%]">
                        Govardhannathji Energies LLP stands as Asia's leading
                        large-scale biogas and bio-energy pioneer, transforming
                        organic waste into renewable Bio CNG and premium organic
                        fertilizers under the UTPANN brand.
                      </p>
                    </div>
                    <Link to="/about">
                      <Button
                        text="Explore Our Solutions"
                        text_font_size="16"
                        text_font_family="Sora"
                        text_font_weight="700"
                        text_line_height="20px"
                        text_text_align="center"
                        text_color="#ffffff"
                        border_border="1px solid #ffffff"
                        border_border_radius="30px"
                        padding="6px 17px"
                        className=" mt-[20px] w-auto text-[16px] sm:text-[20px] 2xl:text-[24px] leading-[20px] sm:leading-[25px] 2xl:leading-[31px] py-[6px] px-[17px] sm:py-[9px] sm:px-[25px] 2xl:py-[12px] 2xl:px-[34px] mt-[11px] sm:mt-[16px] 2xl:mt-[22px] button-focus "
                      />
                    </Link>
                  </div>

                  {/* Right Content - Statistics */}

                  <div className="flex flex-row justify-center items-center w-full lg:w-[46%] animated-hero-text">
                    <div className="hidden 2xl:block w-[1px] h-[149px] sm:h-[223px] 2xl:h-[298px] bg-white about-card-divider"></div>

                    <div className="flex flex-row justify-center items-center w-[92%] ml-[1px]">
                      {/* First Stat */}

                      <div className="flex flex-col gap-[5px] sm:gap-[7px] 2xl:gap-[10px] justify-start items-end px-[28px] sm:px-[42px] 2xl:px-[56px_58px]">
                        <div className="overflow-hidden">
                          <h3 className="text-[40px] sm:text-[60px] 2xl:text-[80px] font-bold leading-[50px] sm:leading-[75px] 2xl:leading-[101px] text-center text-white font-[Sora] mr-[11px] sm:mr-[16px] 2xl:mr-[22px] ">
                            <Counter endValue={821} suffix="k+" />
                          </h3>
                        </div>

                        <div className="overflow-hidden w-full">
                          <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-center text-white font-[Sora] ">
                            MT
                            <br />
                            Waste Mitigation
                          </p>
                        </div>
                      </div>

                      {/* Second Stat */}

                      <div className="flex flex-col gap-[5px] sm:gap-[7px] 2xl:gap-[10px] justify-start items-end px-[28px] sm:px-[42px] 2xl:px-[56px_58px]">
                        <div className="overflow-hidden">
                          <h3 className="text-[40px] sm:text-[60px] 2xl:text-[80px] font-bold leading-[50px] sm:leading-[75px] 2xl:leading-[101px] text-center text-white font-[Sora] mr-[11px] sm:mr-[16px] 2xl:mr-[22px] ">
                            <Counter endValue={23} suffix="k+" />
                          </h3>
                        </div>

                        <div className="overflow-hidden w-full">
                          <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-center text-white font-[Sora] ">
                            MT
                            <br />
                            CBG Produced
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
